'use client';

import { Checkbox, IcCloseCircle, IcLikeFilled, IconButton, Image, LikeMotion } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { ERROR_CODE_LIKE_UNAUTHORIZED, isApiError } from '@musinsa/services-curator';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { getBrandConfig } from '../../common/brandConfig';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useProductLikeMutation } from '../../common/hooks/useProductLikeMutation';
import type { BaseProductImageItem } from './variants/BaseProductImageItem';

interface Props {
  className?: string;
  item: BaseProductImageItem;
  checked?: boolean;
  index?: number;
  onClickProduct: () => void;
  onClickCheckbox?: () => void; // 체크박스 클릭 핸들러
  onClickCloseButton?: () => void; // 닫기 버튼 클릭 핸들러
  onClickLikeButton?: (goodsNo: string | number, isLiked: boolean) => void;
}

export const ProductImageWrapper = ({
  className,
  item,
  checked,
  index,
  onClickProduct,
  onClickCheckbox,
  onClickCloseButton,
  onClickLikeButton,
}: Props) => {
  const { isMobile, service } = useDeviceInfo();
  const { productImageAspectRatio } = getBrandConfig(service);
  const [isLiked, setIsLiked] = useState<boolean>(item.isLiked);
  const { navigate } = useNavigate();

  const productLikeMutation = useProductLikeMutation({
    onSuccess: ({ goodsNo, isLiked: nextIsLiked }) => {
      onClickLikeButton?.(goodsNo, nextIsLiked);
    },
    onError: (error: unknown, { isLiked: nextIsLiked }) => {
      if (isApiError(error) && error.data?.meta?.errorCode === ERROR_CODE_LIKE_UNAUTHORIZED) {
        const url = createLoginUrl(window.location.href);
        navigate(url);
        return;
      }

      setIsLiked(!nextIsLiked);
    },
  });

  const handleClickLike = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    productLikeMutation.mutate({
      goodsNo: String(item.goodsNo),
      isLiked: newLikedState,
    });
  };

  const handleClickCheckbox = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (onClickCheckbox) {
      onClickCheckbox();
    }
  };

  const handleCloseButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    onClickCloseButton?.();
  };

  return (
    <div className={twMerge(className, 'relative')}>
      <div
        role="button"
        tabIndex={0}
        className="relative w-full"
        style={{ aspectRatio: productImageAspectRatio, fontSize: 0 }}
        onClick={onClickProduct}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onClickProduct();
          }
        }}
      >
        <Image
          src={`${item.imageUrl}?width=${isMobile ? 250 : 400}&q=75&format=webp`}
          alt={item.goodsName}
          className="block object-cover w-full h-full"
          loading={index === 0 ? 'eager' : 'lazy'}
          width={service === SERVICE['29CM'] ? 1 : 5}
          height={service === SERVICE['29CM'] ? 1 : 6}
        />
      </div>

      {/* CheckBoxWrapper */}
      {onClickCheckbox && (
        <div className="absolute top-2 right-2 z-10 inline-flex">
          <Checkbox checked={checked} onClick={handleClickCheckbox} />
        </div>
      )}

      {onClickCloseButton && (
        <IconButton
          type="button"
          size="size20x20"
          onClick={handleCloseButtonClick}
          className="absolute top-2 right-2 z-[100]"
        >
          <IcCloseCircle />
        </IconButton>
      )}

      {/* LikeBoxWrapper */}
      <div className="absolute right-1 bottom-1 z-10 inline-flex">
        <LikeMotion like={item.isLiked}>
          {isLiked ? (
            <IcLikeFilled
              fillColor="red"
              strokeColor="transparent"
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          ) : (
            <IcLikeFilled
              fillColor="gray-500"
              strokeColor="white"
              opacity={0.6}
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          )}
        </LikeMotion>
      </div>
    </div>
  );
};
